import React, { useState } from "react";
import { useEditor } from "@craftjs/core";
import { Text } from "../user/Text";
import { Button } from "../user/Button";
import { Image } from "../user/Image";
import { Container } from "../user/Container";
import { FeatureSection } from "../user/FeatureSection";
import {
  saveToLocalStorage,
  loadFromLocalStorage,
  downloadState,
} from "../../utils/craftState";
import "../../styles/components/editor/Toolbar.css";

export const Toolbar = () => {
  const { connectors, actions, query } = useEditor();
  const [activeTab, setActiveTab] = useState("components");

  // Save the current state to localStorage with compression
  const handleSave = () => {
    const success = saveToLocalStorage(query, "craftjs-demo");
    if (success) {
      alert("Page saved successfully!");
    } else {
      alert("Failed to save the page. Please try again.");
    }
  };

  // Load state from localStorage with decompression
  const handleLoad = () => {
    const success = loadFromLocalStorage(actions, "craftjs-demo");
    if (success) {
      alert("Page loaded successfully!");
    } else {
      alert("No saved page found or failed to load.");
    }
  };

  // Export the current design as a downloadable file
  const handleExport = () => {
    downloadState(query);
  };

  // Import design from a file
  const handleImport = () => {
    // Create a file input element
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".lzpage";

    fileInput.addEventListener("change", (e) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];

      if (file) {
        // We'll implement loadStateFromFile directly here since it's a one-time operation
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            if (event.target?.result) {
              const compressed = event.target.result.toString();
              // Import the compressed state
              const lz = require("lzutf8");
              const json = lz.decompress(lz.decodeBase64(compressed));
              actions.deserialize(json);
              alert("Design imported successfully!");
            }
          } catch (error) {
            console.error("Error loading state from file:", error);
            alert("Failed to import the design. The file might be corrupted.");
          }
        };
        reader.readAsText(file);
      }
    });

    // Trigger the file dialog
    fileInput.click();
  };

  // Clear the canvas
  const handleClear = () => {
    if (
      window.confirm(
        "Are you sure you want to clear the canvas? All changes will be lost."
      )
    ) {
      // Use deserialize with an empty object or a default template to reset
      actions.deserialize(
        '{"ROOT":{"type":"div","isCanvas":true,"props":{},"nodes":[],"linkedNodes":{}}}'
      );
    }
  };

  return (
    <div className="sidebar">
      <div className="action-buttons">
        <button
          onClick={() => actions.history.undo()}
          className="action-btn"
          title="Undo"
        >
          ↩️ Undo
        </button>
        <button
          onClick={() => actions.history.redo()}
          className="action-btn"
          title="Redo"
        >
          ↪️ Redo
        </button>
        <button
          onClick={handleSave}
          className="action-btn"
          title="Save to Browser"
        >
          💾 Save
        </button>
        <button
          onClick={handleLoad}
          className="action-btn"
          title="Load from Browser"
        >
          📂 Load
        </button>
        <button
          onClick={handleExport}
          className="action-btn"
          title="Export to File"
        >
          📤 Export
        </button>
        <button
          onClick={handleImport}
          className="action-btn"
          title="Import from File"
        >
          📥 Import
        </button>
        <button
          onClick={handleClear}
          className="action-btn danger"
          title="Clear Canvas"
        >
          🗑️ Clear
        </button>
      </div>

      <div className="tabs">
        <button
          className={`tab-btn ${activeTab === "components" ? "active" : ""}`}
          onClick={() => setActiveTab("components")}
        >
          Components
        </button>
        <button
          className={`tab-btn ${activeTab === "sections" ? "active" : ""}`}
          onClick={() => setActiveTab("sections")}
        >
          Sections
        </button>
        <button
          className={`tab-btn ${activeTab === "templates" ? "active" : ""}`}
          onClick={() => setActiveTab("templates")}
        >
          Templates
        </button>
      </div>

      {activeTab === "components" && (
        <div className="components-tab">
          <h3>Basic Components</h3>
          <div className="components-grid">
            <div
              ref={(ref) => {
                if (ref) {
                  connectors.create(
                    ref,
                    <Text
                      text="Add Text Here"
                      fontSize={16}
                      color="#000000"
                      textAlign="left"
                      fontWeight="normal"
                      margin="0"
                      padding="0"
                    />
                  );
                }
              }}
              className="component-btn"
            >
              <span className="component-icon">T</span>
              <span>Text</span>
            </div>
            <div
              ref={(ref) => {
                if (ref) {
                  connectors.create(
                    ref,
                    <Button
                      text="Click Me"
                      backgroundColor="#007bff"
                      color="#ffffff"
                      fontSize={16}
                      borderRadius={4}
                      padding="12px 24px"
                      margin="0"
                      url=""
                    />
                  );
                }
              }}
              className="component-btn"
            >
              <span className="component-icon">B</span>
              <span>Button</span>
            </div>
            <div
              ref={(ref) => {
                if (ref) {
                  connectors.create(
                    ref,
                    <Image
                      src="https://via.placeholder.com/400x300"
                      alt="Placeholder Image"
                      width="100%"
                      height="auto"
                      margin="0"
                      padding="0"
                      borderRadius={0}
                    />
                  );
                }
              }}
              className="component-btn"
            >
              <span className="component-icon">🖼️</span>
              <span>Image</span>
            </div>
            <div
              ref={(ref) => {
                if (ref) {
                  connectors.create(
                    ref,
                    <Container
                      background="#ffffff"
                      padding="20px"
                      margin="0"
                      borderRadius={0}
                      width="100%"
                      height="auto"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="flex-start"
                    >
                      <p>Drop components here</p>
                    </Container>
                  );
                }
              }}
              className="component-btn"
            >
              <span className="component-icon">🔲</span>
              <span>Container</span>
            </div>
          </div>

          <h3>Layout Components</h3>
          <div className="components-grid">
            <div
              ref={(ref) => {
                if (ref) {
                  connectors.create(
                    ref,
                    <Container
                      background="transparent"
                      padding="20px"
                      margin="0"
                      borderRadius={0}
                      width="100%"
                      height="auto"
                      flexDirection="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <p>Row Layout</p>
                    </Container>
                  );
                }
              }}
              className="component-btn"
            >
              <span className="component-icon">↔️</span>
              <span>Row</span>
            </div>
            <div
              ref={(ref) => {
                if (ref) {
                  connectors.create(
                    ref,
                    <Container
                      background="transparent"
                      padding="20px"
                      margin="0"
                      borderRadius={0}
                      width="100%"
                      height="auto"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <p>Column Layout</p>
                    </Container>
                  );
                }
              }}
              className="component-btn"
            >
              <span className="component-icon">↕️</span>
              <span>Column</span>
            </div>
            <div
              ref={(ref) => {
                if (ref) {
                  connectors.create(
                    ref,
                    <div
                      style={{
                        width: "100%",
                        height: "2px",
                        background: "#e0e0e0",
                        margin: "20px 0",
                      }}
                    ></div>
                  );
                }
              }}
              className="component-btn"
            >
              <span className="component-icon">—</span>
              <span>Divider</span>
            </div>
          </div>

          <h3>Section Components</h3>
          <div className="components-grid">
            <div
              ref={(ref) => {
                if (ref) {
                  connectors.create(ref, <FeatureSection />);
                }
              }}
              className="component-btn"
            >
              <span className="component-icon">✨</span>
              <span>Why Choose Us</span>
            </div>
            <div
              ref={(ref) => {
                if (ref) {
                  connectors.create(
                    ref,
                    <Container
                      background="url(https://images.unsplash.com/photo-1504805572947-34fad45aed93?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80)"
                      height="500px"
                      className="hero-section"
                      padding="20px"
                      margin="0"
                      borderRadius={0}
                      width="100%"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Container
                        className="hero-content"
                        background="transparent"
                        padding="20px"
                        margin="0"
                        borderRadius={0}
                        width="100%"
                        height="auto"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text
                          text="Welcome to Our Website"
                          fontSize={48}
                          color="#ffffff"
                          fontWeight="bold"
                          textAlign="center"
                          className="hero-title"
                          margin="0"
                          padding="0"
                        />
                        <Text
                          text="Learn more about our amazing services"
                          fontSize={24}
                          color="#ffffff"
                          textAlign="center"
                          className="hero-subtitle"
                          fontWeight="normal"
                          margin="10px 0 0 0"
                          padding="0"
                        />
                        <Button
                          text="Get Started"
                          padding="15px 30px"
                          fontSize={18}
                          margin="20px 0 0 0"
                          backgroundColor="#007bff"
                          color="#ffffff"
                          borderRadius={4}
                          url=""
                        />
                      </Container>
                    </Container>
                  );
                }
              }}
              className="section-btn"
            >
              <span className="section-preview">Hero Section</span>
            </div>

            <div
              ref={(ref) => {
                if (ref) {
                  connectors.create(
                    ref,
                    <Container
                      className="feature-section"
                      padding="60px 20px"
                      background="#ffffff"
                      width="100%"
                    >
                      <Text
                        text="Our Features"
                        fontSize={36}
                        color="#333333"
                        fontWeight="bold"
                        textAlign="center"
                        margin="0 0 40px 0"
                      />
                      <Container
                        className="feature-container"
                        flexDirection="row"
                        justifyContent="space-between"
                        width="100%"
                      >
                        {[1, 2, 3].map((item) => (
                          <Container
                            key={item}
                            className="feature-column"
                            padding="20px"
                            width="30%"
                            background="#f8f9fa"
                            borderRadius={8}
                          >
                            <Text
                              text="Feature Title"
                              fontSize={24}
                              color="#333333"
                              fontWeight="bold"
                              textAlign="center"
                              className="feature-title"
                              margin="0 0 15px 0"
                            />
                            <Text
                              text="Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam in dui mauris."
                              fontSize={16}
                              color="#666666"
                              textAlign="center"
                              className="feature-description"
                            />
                          </Container>
                        ))}
                      </Container>
                    </Container>
                  );
                }
              }}
              className="section-btn"
            >
              <span className="section-preview">Feature Section</span>
            </div>

            <div
              ref={(ref) => {
                if (ref) {
                  connectors.create(
                    ref,
                    <Container
                      className="cta-section"
                      background="#f8f9fa"
                      padding="60px 20px"
                      width="100%"
                    >
                      <Container className="cta-content" alignItems="center">
                        <Text
                          text="Ready to Get Started?"
                          fontSize={36}
                          fontWeight="bold"
                          textAlign="center"
                          className="cta-title"
                          margin="0 0 20px 0"
                        />
                        <Text
                          text="Join thousands of satisfied customers today."
                          fontSize={18}
                          textAlign="center"
                          className="cta-description"
                          margin="0 0 30px 0"
                        />
                        <Button
                          text="Sign Up Now"
                          padding="15px 30px"
                          fontSize={18}
                          margin="0"
                          backgroundColor="#007bff"
                          color="#ffffff"
                          borderRadius={4}
                        />
                      </Container>
                    </Container>
                  );
                }
              }}
              className="section-btn"
            >
              <span className="section-preview">CTA Section</span>
            </div>

            <div
              ref={(ref) => {
                if (ref) {
                  connectors.create(
                    ref,
                    <Container
                      className="footer-section"
                      background="#333333"
                      padding="40px 20px"
                      width="100%"
                    >
                      <Container className="footer-content" alignItems="center">
                        <Text
                          text="© 2025 Your Company"
                          fontSize={16}
                          color="#ffffff"
                          textAlign="center"
                        />
                        <Container className="footer-links" margin="20px 0">
                          <Text
                            text="Privacy Policy | Terms of Service | Contact Us"
                            fontSize={14}
                            color="#cccccc"
                            textAlign="center"
                          />
                        </Container>
                        <Text
                          text="All rights reserved. This landing page was created with the Landing Page Builder."
                          fontSize={12}
                          color="#999999"
                          textAlign="center"
                          className="footer-disclaimer"
                        />
                      </Container>
                    </Container>
                  );
                }
              }}
              className="section-btn"
            >
              <span className="section-preview">Footer</span>
            </div>
          </div>
        </div>
      )}

      {activeTab === "templates" && (
        <div className="templates-tab">
          <div className="template-item">
            <h4>Business Template</h4>
            <button
              className="template-btn"
              onClick={() => {
                // This would load a pre-defined template
                const template = localStorage.getItem("business-template");
                if (template) {
                  actions.deserialize(template);
                } else {
                  alert(
                    "Template not found. Save a design as a template first."
                  );
                }
              }}
            >
              Load Template
            </button>
          </div>

          <div className="template-item">
            <h4>Portfolio Template</h4>
            <button
              className="template-btn"
              onClick={() => {
                // This would load a pre-defined template
                const template = localStorage.getItem("portfolio-template");
                if (template) {
                  actions.deserialize(template);
                } else {
                  alert(
                    "Template not found. Save a design as a template first."
                  );
                }
              }}
            >
              Load Template
            </button>
          </div>

          <div className="template-item">
            <h4>Save Current as Template</h4>
            <button
              className="template-btn"
              onClick={() => {
                const name = prompt("Enter a name for your template:");
                if (name) {
                  const json = query.serialize();
                  localStorage.setItem(`${name}-template`, json);
                  alert(`Template "${name}" saved successfully!`);
                }
              }}
            >
              Save as Template
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
